import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Referral, ReferralStage, ReferralSummary, SyncStatus, RateLimit } from '@/types';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ReferralStageBadge } from '@/components/ReferralStageBadge';
import { SyncStatusBanner } from '@/components/SyncStatusBanner';
import { RefreshButton } from '@/components/RefreshButton';
import { formatDateOnly, formatCurrency, cn } from '@/lib/utils';
import { Loader2, UserPlus, Link2 } from 'lucide-react';

type Filter = 'all' | 'pipeline' | 'paying' | 'closed';

// Mirrors PIPELINE_STAGES on the backend so the tile and the filter agree
const FILTER_STAGES: Record<Filter, ReferralStage[] | null> = {
  all: null,
  pipeline: ['lead', 'trial', 'signed_up'],
  paying: ['paying'],
  closed: ['closed', 'lost'],
};

export function ReferralsPage() {
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [summary, setSummary] = useState<ReferralSummary | null>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [rateLimit, setRateLimit] = useState<RateLimit | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.getReferrals();
      setReferrals(response.referrals);
      setSummary(response.summary);
      setSyncStatus(response.sync_status);
      setRateLimit(response.rate_limit);
    } catch (err) {
      console.error('Failed to load referrals:', err);
      setError('Could not load your referrals. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const result = await api.refreshReferrals();
      setRateLimit(result.rate_limit);
      await load();
      if (!result.success) {
        setError(
          result.error === 'Sync already in progress'
            ? 'An update is already running. Your referrals will refresh in a moment.'
            : "We could not update your referrals from Abby's records. We will retry automatically."
        );
      }
    } catch (err) {
      const apiError = err as { error?: string; rate_limit?: RateLimit };
      if (apiError.rate_limit) setRateLimit(apiError.rate_limit);
      setError(apiError.error || 'Refresh failed');
    } finally {
      setIsRefreshing(false);
    }
  };

  const stages = FILTER_STAGES[filter];
  const visible = stages ? referrals.filter((r) => stages.includes(r.stage)) : referrals;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Referrals</h1>
            <p className="text-gray-600 mt-1">Every business you have sent to Abby and where each one stands</p>
          </div>
          <Link to="/toolkit">
            <Button variant="outline">
              <Link2 className="h-4 w-4 mr-2" />
              Referral Toolkit
            </Button>
          </Link>
        </div>

        {syncStatus && <SyncStatusBanner syncStatus={syncStatus} />}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
        )}

        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Tile label="In the pipeline" value={summary.pipeline} hint="leads, trials and sign-ups not yet paying" tone="blue" />
            <Tile label="Paying now" value={summary.paying} hint="active referred clients" tone="green" />
            <Tile
              label="Signed up to date"
              value={summary.signed_up}
              hint={`${summary.conversion_pct}% of ${summary.total} referrals`}
              tone="gray"
            />
            <Tile label="Did not sign up" value={summary.lost} hint="leads that closed without an account" tone="red" />
          </div>
        )}

        <Card>
          <CardHeader>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle>All referrals</CardTitle>
                <CardDescription>
                  Updated from Abby's records every 15 minutes. Contact details stay with Abby.
                </CardDescription>
              </div>
              <div className="flex items-center gap-3">
                <div className="inline-flex rounded-lg bg-gray-100 p-1 text-sm">
                  {(['all', 'pipeline', 'paying', 'closed'] as Filter[]).map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setFilter(f)}
                      className={cn(
                        'px-3 py-1 rounded-md capitalize',
                        filter === f ? 'bg-white shadow text-gray-900 font-medium' : 'text-gray-600'
                      )}
                    >
                      {f}
                    </button>
                  ))}
                </div>
                {rateLimit && (
                  <RefreshButton onClick={handleRefresh} loading={isRefreshing} rateLimit={rateLimit} />
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-abby-primary" />
              </div>
            ) : visible.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <UserPlus className="h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600">
                  {referrals.length === 0
                    ? 'No referrals yet. Share your link from the Referral Toolkit to get started.'
                    : 'No referrals match this filter.'}
                </p>
              </div>
            ) : (
              <>
                {/* Mobile card view */}
                <div className="lg:hidden space-y-4">
                  {visible.map((r) => (
                    <Link
                      key={r.id}
                      to={`/referrals/${r.id}`}
                      className="block border rounded-lg p-4 hover:bg-gray-50"
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <p className="font-medium text-gray-900">{r.name}</p>
                        <ReferralStageBadge stage={r.stage} label={r.stage_label} />
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-gray-500">Referred</p>
                          <p className="text-gray-900">{r.referred_on ? formatDateOnly(r.referred_on) : '-'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Signed up</p>
                          <p className="text-gray-900">{r.signed_up_on ? formatDateOnly(r.signed_up_on) : '-'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Latest monthly</p>
                          <p className="text-gray-900">
                            {r.latest_monthly_amount != null ? formatCurrency(r.latest_monthly_amount) : '-'}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {/* Desktop table view */}
                <div className="hidden lg:block">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left text-sm text-gray-500">
                        <th className="pb-3 font-medium">Business</th>
                        <th className="pb-3 font-medium">Stage</th>
                        <th className="pb-3 font-medium">Referred</th>
                        <th className="pb-3 font-medium">Signed up</th>
                        <th className="pb-3 font-medium text-right">Latest monthly</th>
                        <th className="pb-3 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {visible.map((r) => (
                        <tr key={r.id} className="hover:bg-gray-50">
                          <td className="py-4">
                            <p className="font-medium text-gray-900">{r.name}</p>
                          </td>
                          <td className="py-4">
                            <ReferralStageBadge stage={r.stage} label={r.stage_label} />
                          </td>
                          <td className="py-4 text-sm text-gray-600">
                            {r.referred_on ? formatDateOnly(r.referred_on) : '-'}
                          </td>
                          <td className="py-4 text-sm text-gray-600">
                            {r.signed_up_on ? formatDateOnly(r.signed_up_on) : '-'}
                          </td>
                          <td className="py-4 text-sm text-gray-900 text-right tabular-nums">
                            {r.latest_monthly_amount != null ? formatCurrency(r.latest_monthly_amount) : '-'}
                          </td>
                          <td className="py-4 text-right">
                            <Link to={`/referrals/${r.id}`}>
                              <Button variant="ghost" size="sm">
                                View
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

function Tile({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number;
  hint: string;
  tone: 'blue' | 'green' | 'gray' | 'red';
}) {
  const tones = {
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    green: 'bg-green-50 border-green-200 text-green-800',
    gray: 'bg-gray-50 border-gray-200 text-gray-800',
    red: 'bg-red-50 border-red-200 text-red-800',
  };
  return (
    <div className={cn('rounded-lg border p-4', tones[tone])}>
      <p className="text-sm font-medium mb-1">{label}</p>
      <p className="text-2xl font-bold tabular-nums">{value}</p>
      <p className="text-xs opacity-80 mt-1">{hint}</p>
    </div>
  );
}
